import React, { useContext } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { TourProvider } from './contexts/TourContext';
import Tour from './components/Tour';
import Login from "./pages/Login";
import AuthVerify from "./pages/AuthVerify";
import Dashboard from './pages/Dashboard';
import Canvas from './pages/Canvas';
import MyDecks from './pages/MyDecks';
import Profile from './pages/Profile';
import Admin from './pages/Admin';
import StoryGenerator from './pages/StoryGenerator';
import Loading from './components/Loading';
import Navigation from './components/Navigation';
import { GlobalStyles } from './styles/GlobalStyles';
import { Toaster } from 'react-hot-toast';
import ErrorBoundary from './components/ErrorBoundary';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return <Loading />;
  }
  return user ? children : <Navigate to="/login" replace />;
};

// Admin Route Component
const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return <Loading />;
  }
  return user && user.role === 'admin' ? children : <Navigate to="/dashboard" replace />;
};

const RootRoute = () => {
  const { user } = useAuth();
  return user ? <Navigate to="/dashboard" replace /> : <Navigate to="/login" replace />;
};

const AppWithRouter = () => {
  const location = useLocation();
  // Hamburger menu for Canvas screen
  const isCanvasScreen = location.pathname.startsWith('/canvas');
  return (
    <AuthProvider>
      <TourProvider>
        <GlobalStyles />
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            height: '100vh',
            width: '100vw',
            margin: 0,
            padding: 0,
          }}
        >
          {/* Top Menu: hamburger on canvas, full menu elsewhere */}
          {isCanvasScreen ? (
            <Navigation hamburger={true} />
          ) : (
            <div style={{ flex: '0 0 60px' }}>
              <Navigation hamburger={false} />
            </div>
          )}
          {/* Main Content */}
          <div style={{ minHeight: 0, height: '100vh', display: 'flex', flexDirection: 'column' }}>
            <Routes>
              <Route path="/" element={<RootRoute />} />
              <Route path="/login" element={<Login />} />
              <Route path="/auth/verify" element={<AuthVerify />} />
              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } />
              <Route path="/canvas" element={
                <ProtectedRoute>
                  <Canvas />
                </ProtectedRoute>
              } />
              <Route path="/canvas/:canvasId" element={
                <ProtectedRoute>
                  <Canvas />
                </ProtectedRoute>
              } />
              <Route path="/decks" element={
                <ProtectedRoute>
                  <MyDecks />
                </ProtectedRoute>
              } />
              <Route path="/profile" element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              } />
              <Route path="/story" element={
                <ProtectedRoute>
                  <StoryGenerator />
                </ProtectedRoute>
              } />
              <Route path="/admin" element={
                <AdminRoute>
                  <Admin />
                </AdminRoute>
              } />
            </Routes>
          </div>
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#363636',
                color: '#fff',
              },
              success: {
                duration: 3000,
                iconTheme: {
                  primary: '#4a90e2',
                  secondary: '#fff',
                },
              },
              error: {
                duration: 5000,
                iconTheme: {
                  primary: '#e74c3c',
                  secondary: '#fff',
                },
              },
            }}
          />
        </div>
        <Tour />
      </TourProvider>
    </AuthProvider>
  );
};

export default AppWithRouter; 